
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { mockProviders, mockCategories } from '@/data/mockData';
import { supabase } from '@/integrations/supabase/client';
import { Provider } from '@/types/model';
import { Loader2, Heart, MapPin, Star, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';

const Favorites = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [favoriteProviders, setFavoriteProviders] = useState<Provider[]>([]);
  const [user, setUser] = useState<any>(null);

  // Check authentication status and get user data
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setIsLoggedIn(true);
          setUser(session.user);
          // In a real application, you would fetch favorites from Supabase here
          // For now we'll use mock data
          setFavoriteProviders(mockProviders.slice(0, 2));
        } else {
          setIsLoggedIn(false);
          setFavoriteProviders([]);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        toast.error("Fehler beim Laden der Favoriten");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setIsLoggedIn(!!session);
        setUser(session?.user || null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const removeFavorite = (providerId: string) => {
    // In a real application, you would update this in Supabase
    setFavoriteProviders(favoriteProviders.filter(p => p.id !== providerId));
    toast.success("Anbieter aus Favoriten entfernt");
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="p-3 rounded-full bg-chrona-accent/20 mb-4">
          <Loader2 className="h-8 w-8 text-chrona-accent animate-spin" />
        </div>
        <p className="text-chrona-gray-300">Favoriten werden geladen...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <motion.div 
        className="container mx-auto px-4 py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold mb-8 text-center text-chrona-text">Deine Favoriten</h1>
        
        <Card className="max-w-md mx-auto backdrop-blur-xl bg-black/40 border border-white/10 shadow-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-chrona-accent/20 mb-4">
              <Heart className="h-8 w-8 text-chrona-accent" />
            </div>
            <h3 className="text-xl font-medium mb-2 text-chrona-text">Speichere deine Lieblingsanbieter</h3>
            <p className="text-chrona-gray-300 text-center mb-6">
              Melde dich an, um deine Favoriten zu speichern und schneller zu buchen.
            </p>
            <Button 
              className="px-6 py-6 bg-chrona-accent hover:bg-chrona-accent-light text-white rounded-xl shadow-lg shadow-chrona-accent/20 transition-all"
              onClick={() => navigate('/auth')}
            >
              Anmelden
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="container mx-auto px-4 py-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h1 className="text-3xl font-bold mb-2 text-chrona-text">Deine Favoriten</h1>
      <p className="text-chrona-gray-400 mb-8">Verwalte deine gespeicherten Anbieter</p>
      
      {favoriteProviders.length === 0 ? (
        <Card className="backdrop-blur-xl bg-black/40 border border-white/10 shadow-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-chrona-accent/20 mb-4">
              <Heart className="h-8 w-8 text-chrona-accent" />
            </div>
            <h3 className="text-xl font-medium mb-2 text-chrona-text">Keine Favoriten</h3>
            <p className="text-chrona-gray-300 text-center mb-6">
              Du hast noch keine Anbieter zu deinen Favoriten hinzugefügt.
            </p>
            <Button 
              className="px-6 py-2 bg-chrona-accent hover:bg-chrona-accent-light text-white rounded-xl shadow-lg shadow-chrona-accent/20 transition-all"
              onClick={() => navigate('/')}
            >
              Anbieter durchsuchen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favoriteProviders.map(provider => (
            <motion.div
              key={provider.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <Card 
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer backdrop-blur-xl bg-black/40 border border-white/10" 
                onClick={() => navigate(`/provider/${provider.id}`)}
              >
                <div className="h-48 overflow-hidden relative">
                  <img 
                    src={provider.imageUrl || "https://via.placeholder.com/300x200"} 
                    alt={provider.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
                  />
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black/30 to-transparent"></div>
                  <button 
                    className="absolute top-3 right-3 p-2 rounded-full bg-chrona-gray-900/80 text-white hover:bg-chrona-accent transition-colors" 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(provider.id);
                    }}
                  >
                    <Heart className="h-5 w-5 fill-current" />
                  </button>
                </div>
                <CardContent className="p-5">
                  <div className="flex justify-between items-center mb-2">
                    <div className="inline-block px-2 py-1 bg-chrona-accent/20 text-chrona-accent text-xs rounded-full">
                      {mockCategories.find(c => c.id === provider.branche)?.name}
                    </div>
                    <div className="flex items-center">
                      <Star size={14} className="text-chrona-accent fill-chrona-accent mr-1" />
                      <span className="text-xs text-chrona-text">4.9</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 text-chrona-text">{provider.name}</h3>
                  <div className="flex items-center text-chrona-gray-400 mb-3">
                    <MapPin size={14} className="mr-1 flex-shrink-0" />
                    <span className="text-sm truncate">{provider.adresse}</span>
                  </div>
                  <p className="text-sm text-chrona-gray-300 line-clamp-2 mb-4">{provider.beschreibung}</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t border-chrona-gray-800/50">
                    <span className="text-sm text-chrona-gray-300">Ab 29,90 €</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-chrona-accent hover:bg-chrona-accent/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/provider/${provider.id}`);
                      }}
                    >
                      Details
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default Favorites;
